import { queryOptions, useQuery } from '@tanstack/react-query'
import reportApi from 'api/reportApi'
import dayjs from 'dayjs'
import { transformToStartAndEnd } from 'general/helpers/DateHelper'
import Utils from 'general/utils/Utils'
import _ from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'

export const formatReportParams = (params) => {
  const { startDate, endDate, period, ...rest } = params || {}
  let formattedStartDate = startDate
  let formattedEndDate = endDate

  if (period && (!startDate || !endDate)) {
    const dateRange = transformToStartAndEnd(period)
    if (dateRange?.length === 2) {
      formattedStartDate = dateRange[0]
      formattedEndDate = dateRange[1]
    }
  }

  const parseToIso = (val, isEnd = false) => {
    if (!val) return val
    let d
    if (dayjs.isDayjs(val)) {
      d = val
    } else if (typeof val === 'string') {
      if (val.includes('/')) {
        d = dayjs(val, 'DD/MM/YYYY')
      } else {
        d = dayjs(val)
      }
    } else {
      d = dayjs(val)
    }
    if (!d.isValid()) return val
    return isEnd
      ? d.endOf('day').format('YYYY-MM-DDTHH:mm:ssZ')
      : d.startOf('day').format('YYYY-MM-DDTHH:mm:ssZ')
  }

  return {
    ...rest,
    ...(period ? { period } : {}),
    ...(formattedStartDate
      ? { startDate: parseToIso(formattedStartDate, false) }
      : {}),
    ...(formattedEndDate
      ? { endDate: parseToIso(formattedEndDate, true) }
      : {}),
  }
}

const generalReportQueries = {
  base: {
    scope: ['report', 'generalReport'],
  },

  all: () => ({
    ...generalReportQueries.base,
    entity: ['invoicesByTax'],
  }),

  getList: (params) => {
    const formattedParams = formatReportParams(params)
    return queryOptions({
      queryKey: [generalReportQueries.all(), formattedParams],
      queryFn: ({ signal }) =>
        reportApi.getReportBytax(formattedParams, signal),
      staleTime: 1000 * 30,
    })
  },
}

export const useQueryGeneralReport = (filters, sysConfig) => {
  return useQuery({
    ...generalReportQueries.getList(filters),
    select: (res) => {
      let totalTaxMoney = 0
      let totalBeforeTax = 0
      try {
        let dataGeneralReport = res?.data?.taxArray ?? []
        for (let index = 0; index < dataGeneralReport.length; index++) {
          const generalReport = dataGeneralReport[index]
          const invoiceProducts = generalReport?.invoiceProducts ?? []
          for (let indexIP = 0; indexIP < invoiceProducts.length; indexIP++) {
            const invoiceProduct = invoiceProducts?.[indexIP]
            totalTaxMoney =
              totalTaxMoney +
              Number(
                Utils.formatNumber(
                  invoiceProduct?.taxMoney,
                  sysConfig.totalCost,
                )
                  ?.toString()
                  .replace(/\./g, '')
                  .replace(',', '.'),
              )
            totalBeforeTax =
              totalBeforeTax +
              Number(
                Utils.formatNumber(
                  invoiceProduct?.totalUnitPrice,
                  sysConfig.totalCost,
                )
                  ?.toString()
                  .replace(/\./g, '')
                  .replace(',', '.'),
              )
          }
        }
      } catch (error) {
        console.log('error', error)
      }

      return {
        invoicesByTax: _.get(res, 'data.taxArray', []),
        totalRevenueBeforeTax: totalBeforeTax,
        totalTax: totalTaxMoney,
        totalRevenueAfterTax: totalTaxMoney + totalBeforeTax,
      }
    },
  })
}

export const useQueryGeneralReportByTax = (
  filters,
  taxIndex,
  page,
  enabled = true,
) => {
  return useQuery({
    ...generalReportQueries.getList({
      ...filters,
      page,
      limit: 20,
    }),
    enabled,
    select: (res) => _.get(res, `data.taxArray[${taxIndex}]`, {}),
  })
}

export const useLazyGeneralReport = (filters, sysConfig) => {
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isLoadingInitial, setIsLoadingInitial] = useState(true)
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false)
  const [invoicesByTax, setInvoicesByTax] = useState([])
  const [summaryTotals, setSummaryTotals] = useState({
    totalBeforeTax: 0,
    totalTaxMoney: 0,
    totalAfterTax: 0,
  })

  const isFetchingRef = useRef(false)
  const hasMoreRef = useRef(true)
  const pageRef = useRef(1)
  const lastRequestedPageRef = useRef(1)
  const filtersRef = useRef(filters)

  hasMoreRef.current = hasMore
  pageRef.current = page
  filtersRef.current = filters

  const fetchPage = useCallback(async (targetPage, isReset = false) => {
    if (isFetchingRef.current) return
    if (!isReset && !hasMoreRef.current) return

    isFetchingRef.current = true
    if (isReset) {
      setIsLoadingInitial(true)
    } else {
      setIsFetchingNextPage(true)
    }

    try {
      const formatted = formatReportParams(filtersRef.current)
      const params = {
        ...formatted,
        page: targetPage,
        limit: 100,
      }
      const res = await reportApi.getReportBytax(params)
      const dataTaxArray = res?.data?.taxArray ?? []

      let totalFetchedItemsInThisPage = 0
      let hasAnyMorePages = false
      let hasAnyPaginationInfo = false

      dataTaxArray.forEach((taxItem) => {
        if (taxItem?.invoiceProducts && Array.isArray(taxItem.invoiceProducts)) {
          totalFetchedItemsInThisPage += taxItem.invoiceProducts.length
        }
        if (taxItem?.pagination) {
          hasAnyPaginationInfo = true
          if (taxItem.pagination.page < taxItem.pagination.totalPages) {
            hasAnyMorePages = true
          }
        }
      })

      const moreAvailable = hasAnyPaginationInfo
        ? hasAnyMorePages
        : totalFetchedItemsInThisPage > 0

      const isDataEmpty =
        dataTaxArray.length === 0 ||
        (isReset && totalFetchedItemsInThisPage === 0)

      if (isDataEmpty) {
        setHasMore(false)
        hasMoreRef.current = false
        if (isReset) {
          setInvoicesByTax(dataTaxArray)
        }
      } else {
        setHasMore(moreAvailable)
        hasMoreRef.current = moreAvailable
        if (isReset) {
          setInvoicesByTax(dataTaxArray)
          setPage(1)
          pageRef.current = 1
          lastRequestedPageRef.current = 1
        } else {
          setInvoicesByTax((prev) => {
            const merged = [...(prev.length > 0 ? prev : dataTaxArray)]
            dataTaxArray.forEach((newItem, index) => {
              if (!merged[index]) {
                merged[index] = { ...newItem }
              } else {
                const existingProducts = merged[index].invoiceProducts || []
                const newProducts = newItem?.invoiceProducts || []
                merged[index] = {
                  ...merged[index],
                  ...newItem,
                  pagination: newItem?.pagination || merged[index]?.pagination,
                  invoiceProducts: [...existingProducts, ...newProducts],
                }
              }
            })
            return merged
          })
          setPage(targetPage)
          pageRef.current = targetPage
        }
      }

      if (
        res?.data?.totalBeforeTax !== undefined ||
        res?.data?.totalTaxMoney !== undefined ||
        res?.data?.totalAfterTax !== undefined
      ) {
        setSummaryTotals({
          totalBeforeTax: res?.data?.totalBeforeTax ?? 0,
          totalTaxMoney: res?.data?.totalTaxMoney ?? 0,
          totalAfterTax: res?.data?.totalAfterTax ?? 0,
        })
      }
    } catch (error) {
      console.error('Error fetching general report page:', error)
      lastRequestedPageRef.current = pageRef.current
    } finally {
      isFetchingRef.current = false
      setIsLoadingInitial(false)
      setIsFetchingNextPage(false)
    }
  }, [])

  const filterKey = JSON.stringify(formatReportParams(filters))
  useEffect(() => {
    lastRequestedPageRef.current = 1
    fetchPage(1, true)
  }, [filterKey, fetchPage])

  const fetchNextPage = useCallback(() => {
    if (!isFetchingRef.current && hasMoreRef.current) {
      const nextPage = pageRef.current + 1
      if (nextPage > lastRequestedPageRef.current) {
        lastRequestedPageRef.current = nextPage
        fetchPage(nextPage, false)
      }
    }
  }, [fetchPage])

  return {
    invoicesByTax,
    summaryTotals,
    isLoadingInitial,
    isFetchingNextPage,
    hasMore,
    fetchNextPage,
    page,
  }
}

